import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Headphones, Link2, LogOut, MapPin, Plus, Settings, Users } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { logOut } from '../../../services/auth';
import { useWorkspace } from '../WorkspaceContext';
import { getNavItem } from '../navigation';
import WorkspaceNav from './WorkspaceNav';
import BookingLinkActions from './BookingLinkActions';
import BusinessAgentDock from './BusinessAgentDock';
import WorkspaceWelcomeTour from './WorkspaceWelcomeTour';
import AddToHomeScreenSheet from './AddToHomeScreenSheet';

const WorkspaceLayout = () => {
  const { user } = useAuth();
  const { business, locations, activeLocation, setActiveLocationId } = useWorkspace();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const locationMenuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (locationMenuRef.current && !locationMenuRef.current.contains(event.target)) {
        setIsLocationMenuOpen(false);
      }
    };

    if (isUserMenuOpen || isLocationMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen, isLocationMenuOpen]);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Your account';
  const businessName = business?.name || 'Your business';
  const initial = (user?.displayName || user?.email || 'S')[0].toUpperCase();

  return (
    <div className="flex min-h-screen flex-col bg-sand-100 font-body text-ink-900">
      <header className="sticky top-0 z-40">
        <div className="bg-ink-900">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:gap-4 sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <Link to="/" className="flex flex-none items-center gap-2 sm:gap-2.5">
                <div className="h-8 w-8 overflow-hidden rounded-full ring-1 ring-sand-100/25 sm:h-9 sm:w-9">
                  <img src="/logo192.png" alt="" className="h-full w-full scale-110 object-cover" />
                </div>
                <span className="hidden font-display text-lg font-light tracking-tight text-sand-50 sm:inline">
                  Spirara<span className="text-saffron-500">.</span>
                </span>
              </Link>
              <span aria-hidden="true" className="hidden h-6 w-px flex-none bg-sand-100/20 sm:block" />
              <p className="hidden min-w-0 truncate text-sm text-sand-200/80 lg:block">{businessName}</p>

              <div className="relative min-w-0" ref={locationMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsLocationMenuOpen((open) => !open)}
                  aria-expanded={isLocationMenuOpen}
                  className="flex max-w-[9.5rem] items-center gap-1.5 bg-sand-50/10 px-2 py-1.5 text-sm text-sand-100 transition-colors hover:bg-sand-50/20 sm:max-w-[14rem] sm:gap-2 sm:px-3"
                >
                  <MapPin className="h-4 w-4 flex-none text-saffron-400" />
                  <span className="min-w-0 truncate">{activeLocation?.name || 'Location'}</span>
                  <ChevronDown
                    className={`h-4 w-4 flex-none transition-transform ${isLocationMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isLocationMenuOpen && (
                  <div className="absolute left-0 z-50 mt-2 w-64 bg-sand-50 py-2 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10">
                    <p className="px-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-600/60">
                      Locations
                    </p>
                    {locations.map((location) => (
                      <button
                        key={location.id}
                        type="button"
                        onClick={() => {
                          setActiveLocationId(location.id);
                          setIsLocationMenuOpen(false);
                        }}
                        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm text-ink-800 transition-colors hover:bg-ink-900/5"
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{location.name}</span>
                          {(location.city || location.address) && (
                            <span className="block truncate text-xs text-ink-600/70">
                              {location.address || location.city}
                            </span>
                          )}
                        </span>
                        {activeLocation?.id === location.id && (
                          <Check className="h-4 w-4 flex-none text-saffron-600" />
                        )}
                      </button>
                    ))}
                    <div className="my-1 border-t border-ink-900/10" />
                    <Link
                      to="/app/manage/locations"
                      onClick={() => setIsLocationMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-800 transition-colors hover:bg-ink-900/5"
                    >
                      <Plus className="h-4 w-4" />
                      Add location
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-3">
              <BookingLinkActions compact />

              <div className="relative flex-none" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setIsUserMenuOpen((open) => !open)}
                aria-expanded={isUserMenuOpen}
                className="flex items-center gap-2 px-2 py-1.5 text-sand-100 transition-colors hover:bg-sand-50/10"
              >
                {user?.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-saffron-500 text-sm font-semibold text-ink-900">
                    {initial}
                  </span>
                )}
                <span className="hidden max-w-[10rem] truncate text-sm font-medium sm:block">
                  {displayName}
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 z-50 mt-2 w-72 bg-sand-50 py-2 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10">
                  <div className="px-4 pb-2">
                    <p className="truncate text-sm font-semibold text-ink-900">{displayName}</p>
                    <p className="truncate text-xs text-ink-600/70">{user?.email}</p>
                  </div>
                  <div className="border-t border-ink-900/10" />
                  <Link
                    to={`${getNavItem('manage').path}/services`}
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-800 transition-colors hover:bg-ink-900/5"
                  >
                    <Settings className="h-4 w-4" />
                    Business settings
                  </Link>
                  <Link
                    to={`${getNavItem('manage').path}/booking-link`}
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-800 transition-colors hover:bg-ink-900/5 lg:hidden"
                  >
                    <Link2 className="h-4 w-4" />
                    Booking link
                  </Link>
                  <a
                    href="https://wa.me/19848886717"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-800 transition-colors hover:bg-ink-900/5"
                  >
                    <Headphones className="h-4 w-4" />
                    Contact support
                  </a>
                  <a
                    href="https://chat.whatsapp.com/I4cvgbZZfgQHZYKoE9kSmz"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-800 transition-colors hover:bg-ink-900/5"
                  >
                    <Users className="h-4 w-4" />
                    Join our WhatsApp community
                  </a>
                  <div className="my-1 border-t border-ink-900/10" />
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-ink-800 transition-colors hover:bg-red-50 hover:text-red-700"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>

        <WorkspaceNav />
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <BusinessAgentDock />
      <WorkspaceWelcomeTour />
      <AddToHomeScreenSheet />
    </div>
  );
};

export default WorkspaceLayout;
